import type { SpellingElement } from "@ai-teach/types";

/**
 * Which letter boxes of a spelling question are pre-filled and locked for the student, as
 * indexes into `element.word`.
 *
 * `revealedIndexes` is the current shape; `revealFirstLetter` is the boolean it replaced and is
 * still the only thing the API's PDF import writes, so an element without the array falls back to
 * it. Every reader goes through here rather than testing either field directly — scoring,
 * completion, review, and both renderers have to agree on which boxes are locked, and the two
 * shapes drift apart the moment one of them tests the boolean on its own.
 *
 * Indexes past the end of the word are dropped: shortening `word` in the toolbar leaves stale
 * entries behind, and a revealed box that no longer exists would otherwise be excluded from a
 * scoring total it never contributed to.
 */
export function getRevealedIndexes(element: SpellingElement): Set<number> {
  const indexes = element.revealedIndexes ?? (element.revealFirstLetter ? [0] : []);
  return new Set(indexes.filter((index) => index >= 0 && index < element.word.length));
}

/**
 * The element props to store for a new set of revealed indexes.
 *
 * Writes `revealFirstLetter` alongside the array so the two never contradict each other: a client
 * running older code, and the API's PDF import path, still read only the boolean, and would show
 * an un-hinted word where the teacher revealed the first letter.
 */
export function buildRevealProps(
  revealed: Iterable<number>
): Pick<SpellingElement, "revealedIndexes" | "revealFirstLetter"> {
  const revealedIndexes = [...revealed].sort((a, b) => a - b);
  return { revealedIndexes, revealFirstLetter: revealedIndexes.includes(0) };
}
